import type { ChangeMeetingBody, DateChangeDecisionBody, FollowMeetingBody, FollowedMeetingsResponse, ProjectMeetingsResponse } from '@pulse/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

/** Meetings we follow (MT-1). */
export function useFollowedMeetings() {
  return useQuery({ queryKey: ['meetings'], queryFn: () => apiRequest<FollowedMeetingsResponse>('/api/meetings'), staleTime: 60_000 });
}

export type MeetingChange =
  | { readonly kind: 'follow'; readonly body: FollowMeetingBody }
  | { readonly kind: 'change'; readonly meetingId: string; readonly body: ChangeMeetingBody }
  | { readonly kind: 'stop'; readonly meetingId: string };

/** Following a meeting, changing one, or stopping (MT-1). */
export function useChangeFollowedMeetings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (change: MeetingChange): Promise<unknown> => {
      if (change.kind === 'follow') return apiRequest('/api/meetings', { method: 'POST', body: change.body });
      if (change.kind === 'change') return apiRequest(`/api/meetings/${encodeURIComponent(change.meetingId)}`, { method: 'PUT', body: change.body });
      return apiRequest(`/api/meetings/${encodeURIComponent(change.meetingId)}`, { method: 'DELETE' });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['meetings'] }),
  });
}

/** A project's Meetings tab (MT-4). */
export function useProjectMeetings(productSlug: string) {
  return useQuery({
    queryKey: ['meetings', 'project', productSlug],
    queryFn: () => apiRequest<ProjectMeetingsResponse>(`/api/meetings/projects/${encodeURIComponent(productSlug)}`),
    staleTime: 60_000,
  });
}

/** Accept or dismiss a date change Talos heard agreed (MT-3). */
export function useDecideDateChange() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: DateChangeDecisionBody) => apiRequest('/api/meetings/changes', { method: 'POST', body }),
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: ['meetings'] }), client.invalidateQueries({ queryKey: ['milestones'] })]),
  });
}
